/* eslint-disable react/jsx-key */
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import { BadgeStatus } from '@cas/components/data-display/badge-dot/BadgeDot';
import Metric, { MetricSize } from '@customer/components/data-display/metric/Metric';
import useAnalyticsSubPage from '@customer/templates/health-sub-page/useAnalyticsSubPage';
import {
  AssetBasicModel,
  AssetListScenarios,
  HealthSoftwareStatusAssetsFilters,
} from '@customer/store/apis/assetsApi';
import { AssetConfigurationAccordion, AssetSidePanelTab } from '@customer/store/slices/globalSlice';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { HealthScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import BarChartByDeviceType from '@customer/components/charts/by-device-type/BarChartByDeviceType';
import InsightsCard from '@customer/components/data-display/insights-card/InsightsCard';
import StackedBar from '@customer/components/charts/stacked-bar/StackedBar';
import {
  columnDefAssetGroup,
  columnDefAssetName,
  columnDefCasSoftware,
  columnDefConfigGroupName,
  columnDefDeviceType,
  columnDefReportingStatus,
  columnDefSoftwareStatus,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef } from 'ag-grid-community';
import SubTabsPageContent from '@customer/templates/sub-tabs-page/SubTabsPageContent';
import FilterSummary from '@customer/components/inputs/filter-summary/FilterSummary';
import FilterButton from '@customer/components/inputs/filter-button/FilterButton';
import { FilterGroup } from '@customer/filters/types';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';
import BarChartByConfigGroup from '@customer/components/charts/by-config-group/BarChartByConfigGroup';

const AssetGridSliceKey = 'AssetsHealthConfigSoftwareStatus';
const filterGroup = FilterGroup.HealthSoftwareStatus;

export function SoftwareStatus() {
  const { t } = useTranslation();

  const [columnDefs] = useState<ColDef<AssetBasicModel>[]>([
    { ...columnDefAssetName(t), maxWidth: 150 },
    { ...columnDefDeviceType(t), maxWidth: 110 },
    { ...columnDefAssetGroup(t) },
    { ...columnDefCasSoftware(t), maxWidth: 150 },
    { ...columnDefConfigGroupName(t), maxWidth: 250 },
    { ...columnDefReportingStatus(t), maxWidth: 150 },
    { ...columnDefSoftwareStatus(t) },
  ]);

  const {
    assets,
    hasData,
    noDataDescription,
    countAll,
    countFilter,
    countScenarioFilter,
    onRowClick,
    isFetching,
  } = useAnalyticsSubPage<HealthSoftwareStatusAssetsFilters>({
    scenario: AssetListScenarios.HealthConfigStatus,
    filterGroup: filterGroup,
    sidePanelTab: AssetSidePanelTab.Configuration,
    sidePanelDefaultAccordion: AssetConfigurationAccordion.SoftwareStatus,
  });

  const getDataSetLabel = useCallback((label: string) => t(label), [t]);

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByAssetGroup'),
        ChartComponent: BarChartByAssetGroup<HealthScenarioQueryParams>,
        scenario: StatsScenario.ConfigStatusByAssetGroup,
      },
      {
        title: t('ByDeviceType'),
        ChartComponent: BarChartByDeviceType<HealthScenarioQueryParams>,
        scenario: StatsScenario.ConfigStatusByDeviceType,
      },
      {
        title: t('ByConfigurationGroup'),
        ChartComponent: BarChartByConfigGroup<HealthScenarioQueryParams>,
        scenario: StatsScenario.ConfigStatusByConfigGroup,
      },
    ],
    [t],
  );

  return (
    <SubTabsPageContent
      headerSummary={
        <FilterSummary
          transKey="ShowingAssets"
          filteredTransKey="ShowingFilteredAssets"
          countAll={countAll}
          countFilter={countFilter}
          filterGroup={filterGroup}
        />
      }
      headerActions={<FilterButton filters={filterGroup} disabled={isFetching} />}
      body={
        <>
          <InsightsCard
            sections={[
              <Metric
                dataPrimary={countScenarioFilter}
                dataSecondary={countFilter}
                isFraction
                label={
                  <>
                    <span>{t('MetricAssetsOutdatedSoftwareLower')}</span>
                    <StackedBar
                      total={countFilter}
                      portions={[countScenarioFilter ?? 0]}
                      colorScheme={ChartColorScheme.RedGrey}
                    />
                  </>
                }
                size={MetricSize.XL}
              />,
            ]}
          />

          <ChartScenarios
            title={t('SoftwareStatus')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={filterGroup}
            colorScheme={ChartColorScheme.RedGrey}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="AssetsWithCount"
          />

          <AnalyticsTable
            title={t('MetricAssetsOutdatedSoftwareLower')}
            metrics={
              <Metric
                dataPrimary={countScenarioFilter}
                dataSecondary={countFilter}
                isFraction
                status={BadgeStatus.Danger}
              />
            }
            hasData={hasData}
            noDataDescription={noDataDescription}
          >
            <GridControlled<AssetBasicModel>
              sliceKey={AssetGridSliceKey}
              rowData={assets}
              columnDefs={columnDefs}
              noRowsMessage={t('No assets')}
              onRowClick={onRowClick}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default SoftwareStatus;
